import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Upload, Download } from 'lucide-react';
import toast from 'react-hot-toast';

// Update question types to match backend format
const QUESTION_TYPES = {
  text: 'Text',
  multiple_choice: 'Multiple Choice',
  checkbox: 'Checkbox',
  rating: 'Rating Scale',
  likert: 'Likert Scale',
  dropdown: 'Dropdown',
  yes_no: 'Yes/No',
  numeric: 'Numeric',
  date: 'Date',
  email: 'Email',
  file_upload: 'File Upload'
};

// Define question types that should be excluded from the dropdown
const EXCLUDED_QUESTION_TYPES = ['Email', 'File Upload'];

// Add a constant for question types that need options
const QUESTION_TYPES_WITH_OPTIONS = [
  'Multiple Choice',
  'Checkbox',
  'Dropdown',
  'Likert Scale',
  'Rating Scale'
];

// Function to convert backend type to display type
const getDisplayType = (backendType) => QUESTION_TYPES[backendType] || backendType;

// Function to convert display type to backend type
const getBackendType = (displayType) => {
  return Object.entries(QUESTION_TYPES).find(([_, value]) => value === displayType)?.[0] || displayType.toLowerCase();
};

// Add a helper to check if a question type needs options
const needsOptions = (questionType) => QUESTION_TYPES_WITH_OPTIONS.includes(questionType);

const SurveyBuilder = ({ survey = null, surveyType = 'standard', onCreateSurvey, onUpdateSurvey, onCancel, initialStudyId = null, loading = false }) => {
  const [surveyData, setSurveyData] = useState({
    name: '',
    short_description: '',
    long_description: '',
    status: 'draft',
    is_hidden: false,
    rank: 0,
    survey_type: surveyType,
    respondent_type: 'participant',
    questions: []
  });

  const [jsonError, setJsonError] = useState(null);

  useEffect(() => {
    if (survey) {
      // Transform the questions to match the expected format
      const transformedQuestions = (survey.questions || []).map(q => ({
        question_text: q.text || q.question_text || '',
        question_type: getDisplayType(q.type || q.question_type || 'text'),
        options: Array.isArray(q.options) ? q.options : 
                q.options && typeof q.options === 'object' ? 
                  q.type === 'rating' || q.type === 'likert' ? 
                    { min: q.options.min_value || 1, max: q.options.max_value || 5, labels: q.options.labels || [] } :
                    Object.values(q.options) :
                  [],
        required: q.required || false,
        category: q.category,
        slope: q.slope,
        thresholds: q.thresholds

      }));

      setSurveyData({
        id: survey.id,
        name: survey.name || '',
        short_description: survey.short_description || '',
        long_description: survey.long_description || '',
        status: survey.status || 'draft',
        is_hidden: survey.is_hidden || false,
        rank: survey.rank || 0,
        survey_type: survey.survey_type || 'standard',
        respondent_type: survey.respondent_type || 'participant',
        questions: transformedQuestions,
        adaptive_config: survey.adaptive_config,
      });
    }
  }, [survey]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSurveyData(prev => ({ ...prev, [name]: value }));
  };

  const addQuestion = () => {
    // Find the first valid question type (not excluded)
    const defaultQuestionType = Object.values(QUESTION_TYPES).find(type => !EXCLUDED_QUESTION_TYPES.includes(type)) || 'Text';
    
    setSurveyData(prev => ({
      ...prev,
      questions: [...prev.questions, {
        question_text: '',
        question_type: defaultQuestionType,
        options: [],
        required: false
      }]
    }));
  };

  const handleQuestionChange = (index, field, value) => {
    setSurveyData(prev => {
      const newQuestions = [...prev.questions];
      newQuestions[index] = { ...newQuestions[index], [field]: value };
      return { ...prev, questions: newQuestions };
    });
  };

  const handleOptionChange = (questionIndex, optionIndex, value) => {
    setSurveyData(prev => {
      const newQuestions = [...prev.questions];
      const options = [...newQuestions[questionIndex].options];
      options[optionIndex] = value;
      newQuestions[questionIndex] = { ...newQuestions[questionIndex], options };
      return { ...prev, questions: newQuestions };
    });
  };

  const addOption = (questionIndex) => {
    setSurveyData(prev => {
      const newQuestions = [...prev.questions];
      newQuestions[questionIndex].options = [...(newQuestions[questionIndex].options || []), ''];
      return { ...prev, questions: newQuestions };
    });
  };

  const removeQuestion = (index) => {
    setSurveyData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const removeOption = (questionIndex, optionIndex) => {
    setSurveyData(prev => {
      const newQuestions = [...prev.questions];
      newQuestions[questionIndex].options = newQuestions[questionIndex].options.filter((_, i) => i !== optionIndex);
      return { ...prev, questions: newQuestions };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Original survey data:', surveyData);
    
    // Validate that all questions have types
    const missingTypes = surveyData.questions.filter(q => !q.question_type);
    if (missingTypes.length > 0) {
      console.error('Some questions are missing types:', missingTypes);
      toast.error('All questions must have a type selected', {
        duration: 4000,
        position: 'top-center'
      });
      return;
    }

    // Check for duplicate question text
    const duplicateQuestions = [];
    const questionTexts = new Map();
    surveyData.questions.forEach((q, index) => {
      const trimmedText = q.question_text?.trim().toLowerCase();
      if (trimmedText && trimmedText.length > 0) {
        if (questionTexts.has(trimmedText)) {
          const originalIndex = questionTexts.get(trimmedText);
          duplicateQuestions.push({
            original: originalIndex + 1,
            duplicate: index + 1,
            text: q.question_text.trim()
          });
        } else {
          questionTexts.set(trimmedText, index);
        }
      }
    });

    if (duplicateQuestions.length > 0) {
      const errorMessage = duplicateQuestions.length === 1 
        ? `Questions ${duplicateQuestions[0].original} and ${duplicateQuestions[0].duplicate} have identical text: "${duplicateQuestions[0].text}"`
        : `Multiple questions have identical text:\n${duplicateQuestions.map(d => `• Questions ${d.original} and ${d.duplicate}: "${d.text}"`).join('\n')}`;
      toast.error(errorMessage, {
        duration: 8000,
        position: 'top-center',
        style: {
          whiteSpace: 'pre-line'
        }
      });
      return;
    }

    // Validate rating/likert scale ranges
    const invalidRanges = [];
    const largeRanges = [];
    surveyData.questions.forEach((q, index) => {
      if (q.question_type === 'Rating Scale' || q.question_type === 'Likert Scale') {
        const min = q.options?.min || 1;
        const max = q.options?.max || 5;
        
        if (min >= max) {
          invalidRanges.push({
            index: index + 1,
            type: q.question_type,
            min,
            max
          });
        } else if ((max - min) > 20) {
          largeRanges.push({
            index: index + 1,
            type: q.question_type,
            range: max - min + 1
          });
        }
      }
    });

    if (invalidRanges.length > 0) {
      const errorMessage = invalidRanges.length === 1 
        ? `Question ${invalidRanges[0].index} (${invalidRanges[0].type}) has invalid range: minimum (${invalidRanges[0].min}) must be less than maximum (${invalidRanges[0].max}).`
        : `The following questions have invalid ranges:\n${invalidRanges.map(r => `• Question ${r.index} (${r.type}): min ${r.min} >= max ${r.max}`).join('\n')}`;
      toast.error(errorMessage, {
        duration: 6000,
        position: 'top-center',
        style: {
          whiteSpace: 'pre-line'
        }
      });
      return;
    }

    // Warn about large ranges (but don't block submission)
    if (largeRanges.length > 0) {
      const warningMessage = largeRanges.length === 1 
        ? `Question ${largeRanges[0].index} (${largeRanges[0].type}) has a large range of ${largeRanges[0].range} options. Consider using a smaller range for better user experience.`
        : `The following questions have large ranges:\n${largeRanges.map(r => `• Question ${r.index} (${r.type}): ${r.range} options`).join('\n')}\n\nConsider using smaller ranges for better user experience.`;
      toast(warningMessage, {
        duration: 8000,
        position: 'top-center',
        style: {
          whiteSpace: 'pre-line',
          backgroundColor: '#f59e0b',
          color: 'white'
        }
      });
    }

    // Validate numeric questions for min/max ranges
    const invalidNumericQuestions = [];
    surveyData.questions.forEach((q, index) => {
      if (q.question_type === 'Numeric') {
        const min = q.options?.min;
        const max = q.options?.max;
        
        // Check if both min and max are provided and min >= max
        if (min !== undefined && max !== undefined && min >= max) {
          invalidNumericQuestions.push({
            index: index + 1,
            min,
            max
          });
        }
      }
    });

    if (invalidNumericQuestions.length > 0) {
      const errorMessage = invalidNumericQuestions.length === 1 
        ? `Question ${invalidNumericQuestions[0].index} (Numeric) has invalid range: minimum (${invalidNumericQuestions[0].min}) must be less than maximum (${invalidNumericQuestions[0].max}).`
        : `The following numeric questions have invalid ranges:\n${invalidNumericQuestions.map(n => `• Question ${n.index}: min ${n.min} >= max ${n.max}`).join('\n')}`;
      toast.error(errorMessage, {
        duration: 6000,
        position: 'top-center',
        style: {
          whiteSpace: 'pre-line'
        }
      });
      return;
    }

    // Validate email questions for basic format validation
    const invalidEmailQuestions = [];
    surveyData.questions.forEach((q, index) => {
      if (q.question_type === 'Email') {
        // Check if there's a default value or placeholder that should be validated
        const defaultValue = q.options?.default_value || q.options?.placeholder;
        if (defaultValue && defaultValue.trim()) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(defaultValue.trim())) {
            invalidEmailQuestions.push({
              index: index + 1,
              value: defaultValue.trim()
            });
          }
        }
      }
    });

    if (invalidEmailQuestions.length > 0) {
      const errorMessage = invalidEmailQuestions.length === 1 
        ? `Question ${invalidEmailQuestions[0].index} (Email) has invalid default email format: "${invalidEmailQuestions[0].value}"`
        : `The following email questions have invalid default formats:\n${invalidEmailQuestions.map(e => `• Question ${e.index}: "${e.value}"`).join('\n')}`;
      toast.error(errorMessage, {
        duration: 6000,
        position: 'top-center',
        style: {
          whiteSpace: 'pre-line'
        }
      });
      return;
    }

    // Validate that choice-based questions have options
    const invalidQuestions = [];
    surveyData.questions.forEach((q, index) => {
      if (needsOptions(q.question_type)) {
        let hasValidOptions = false;
        
        if (q.question_type === 'Rating Scale') {
          // For rating scales, check the labels array
          const labels = q.options?.labels || [];
          const validLabels = Array.isArray(labels) ? labels.filter(label => label && label.trim() !== '') : [];
          hasValidOptions = validLabels.length >= 2;
        } else {
          // For other question types (including Likert Scale), check options array
          const validOptions = Array.isArray(q.options) ? q.options.filter(opt => opt.trim() !== '') : [];
          hasValidOptions = validOptions.length >= 2;
        }
        
        if (!hasValidOptions) {
          invalidQuestions.push({
            index: index + 1,
            type: q.question_type,
            text: q.question_text || 'Untitled Question'
          });
        }
      }
    });

    if (invalidQuestions.length > 0) {
      const errorMessage = invalidQuestions.length === 1 
        ? `Question ${invalidQuestions[0].index} (${invalidQuestions[0].type}) must have at least two options.`
        : `The following questions need at least two options:\n${invalidQuestions.map(q => `• Question ${q.index} (${q.type})`).join('\n')}`;
      toast.error(errorMessage, {
        duration: 6000,
        position: 'top-center',
        style: {
          whiteSpace: 'pre-line' // Allows line breaks in multi-line messages
        }
      });
      return;
    }

    // Transform the questions back to the backend format
    const transformedQuestions = surveyData.questions.map((q, index) => {
      const backendType = getBackendType(q.question_type);
      const baseQuestion = {
        text: q.question_text,
        type: backendType,
        rank: index + 1,
        is_hidden: false,
        category: q.category,
        slope: q.slope,
        thresholds: q.thresholds
      };

      // Handle options based on question type
      if (backendType === 'rating') {
        baseQuestion.options = {
          min_value: q.options?.min || 1,
          max_value: q.options?.max || 5,
          step: 1,
          labels: q.options?.labels || []
        };
      } else if (Array.isArray(q.options)) {
        baseQuestion.options = q.options;
      } else {
        baseQuestion.options = [];
      }

      return baseQuestion;
    });

    const dataToSave = {
      ...surveyData,
      questions: transformedQuestions
    };

    try {
      if (survey) {
        await onUpdateSurvey(dataToSave);
      } else {
        await onCreateSurvey(dataToSave);
      }
    } catch (error) {
      console.error('Error submitting survey:', error);
      
      // Extract detailed error message from backend response
      let errorMessage = 'Error creating survey';
      
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        // Handle structured validation errors
        if (errorData.detail && typeof errorData.detail === 'object') {
          const validationErrors = [];
          Object.entries(errorData.detail).forEach(([field, messages]) => {
            if (Array.isArray(messages)) {
              messages.forEach(msg => validationErrors.push(`${field}: ${msg}`));
            } else {
              validationErrors.push(`${field}: ${messages}`);
            }
          });
          errorMessage = validationErrors.join('\n');
        }
        // Handle simple error messages
        else if (errorData.error) {
          errorMessage = errorData.error;
          if (errorData.detail && typeof errorData.detail === 'string') {
            errorMessage += ': ' + errorData.detail;
          }
        }
        // Handle direct message
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        duration: 6000,
        position: 'top-center',
        style: {
          whiteSpace: 'pre-line' // Allows line breaks in validation messages
        }
      });
    }
  };

  const handleJsonImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const jsonData = JSON.parse(event.target.result);
        
        // Validate the JSON structure
        if (!jsonData.name || !Array.isArray(jsonData.questions)) {
          throw new Error('Invalid survey format. JSON must include "name" and "questions" array.');
        }

        // Transform the imported data to match the expected format
        const transformedData = {
          name: jsonData.name,
          short_description: jsonData.short_description || '',
          long_description: jsonData.long_description || '',
          status: jsonData.status || 'draft',
          survey_type: jsonData.survey_type || 'standard',
          is_hidden: jsonData.is_hidden || false,
          rank: jsonData.rank || 0,
          version: jsonData.version || 'A',
          adaptive_config: jsonData.adaptive_config,

          questions: jsonData.questions.map(q => ({
            question_text: q.text || q.question_text,
            question_type: getDisplayType(q.type || q.question_type || 'text'),
            options: q.options || [],
            required: q.required || false,
            category: q.category,
            slope: q.slope,
            thresholds: q.thresholds
          }))
        };

        setSurveyData(transformedData);
        setJsonError(null);
      } catch (error) {
        setJsonError('Invalid JSON format.');
      }
    };
    reader.readAsText(file);
  };

  const generateSampleJson = () => {
    const sampleJson = {
      "name": "Sample Clinical Assessment",
      "short_description": "A sample survey demonstrating proper format",
      "long_description": "This is a comprehensive example showing all question types and format requirements for FlexCOA surveys.",
      "status": "draft",
      "survey_type": "standard",
      "is_hidden": false,
      "rank": 1,
      "version": "A",
      "is_managed": false,
      "questions": [
        {
          "text": "What is your full name?",
          "type": "text",
          "rank": 1,
          "is_hidden": false
        },
        {
          "text": "Select your primary symptom:",
          "type": "multiple_choice",
          "options": ["Headache", "Fatigue", "Nausea", "Pain", "Dizziness"],
          "rank": 2,
          "is_hidden": false
        },
        {
          "text": "Which of the following symptoms do you experience? (Select all that apply)",
          "type": "checkbox",
          "options": ["Headache", "Fatigue", "Nausea", "Pain", "Dizziness"],
          "rank": 3,
          "is_hidden": false
        },
        {
          "text": "Rate your overall pain level:",
          "type": "rating",
          "options": {
            "min_value": 0,
            "max_value": 10,
            "step": 1,
            "labels": ["No pain", "Mild", "Moderate", "Severe", "Worst possible pain"]
          },
          "rank": 4,
          "is_hidden": false
        },
        {
          "text": "I feel my treatment plan is helping me:",
          "type": "likert",
          "options": ["Strongly disagree", "Disagree", "Neutral", "Agree", "Strongly agree"],
          "rank": 5,
          "is_hidden": false
        },
        {
          "text": "Select your primary medication:",
          "type": "dropdown",
          "options": ["Ibuprofen", "Acetaminophen", "Naproxen", "Aspirin", "Other"],
          "rank": 6,
          "is_hidden": false
        },
        {
          "text": "Have you experienced any side effects?",
          "type": "yes_no",
          "rank": 7,
          "is_hidden": false
        },
        {
          "text": "How many days per week do you take your medication?",
          "type": "numeric",
          "rank": 8,
          "is_hidden": false
        },
        {
          "text": "When did your symptoms begin?",
          "type": "date",
          "rank": 9,
          "is_hidden": false
        },
        {
          "text": "Please provide your email for follow-up:",
          "type": "email",
          "rank": 10,
          "is_hidden": false
        }
      ]
    };
    
    return JSON.stringify(sampleJson, null, 2);
  };
  
  // Function to handle download of sample JSON
  const handleDownloadSampleJson = () => {
    const sampleJson = generateSampleJson();
    const blob = new Blob([sampleJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_survey.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <h2 className="text-2xl font-semibold text-gray-800">
        {survey 
          ? survey.survey_type === 'missing_form' 
            ? 'Edit Missing Form' 
            : 'Edit Survey'
          : surveyType === 'missing_form' 
            ? 'Create New Missing Form' 
            : 'Create New Survey'
        }
      </h2>

      {/* Existing form content */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">Survey Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={surveyData.name}
            onChange={handleInputChange}
            required
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2.5 px-3.5 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <input type="hidden" name="studies" value={surveyData.studies} />
        <div>
          <label htmlFor="short_description" className="block text-sm font-medium text-gray-700">Short Description</label>
          <input
            type="text"
            id="short_description"
            name="short_description"
            value={surveyData.short_description}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2.5 px-3.5 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label htmlFor="long_description" className="block text-sm font-medium text-gray-700">Long Description</label>
          <textarea
            id="long_description"
            name="long_description"
            value={surveyData.long_description}
            onChange={handleInputChange}
            rows="3"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2.5 px-3.5 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          ></textarea>
        </div>
        {/* Respondent type (PRO/ObsRO/ClinRO) is set from the Schedule Builder
            (FECOA-1320). It's still preserved in state + payload here so editing
            a survey via this legacy form doesn't reset it. */}
        <div className="mb-4 p-4 border border-dashed border-gray-300 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Import from JSON</h3>
          <div className="flex items-center gap-2">
            <label className="flex items-center px-4 py-2 bg-white dark:bg-gray-800 text-blue-600 rounded-lg border border-blue-600 hover:bg-blue-50 cursor-pointer">
              <Upload className="w-4 h-4 mr-2" />
              <span>Choose JSON File</span>
              <input
                type="file"
                accept=".json"
                onChange={handleJsonImport}
                className="hidden"
              />
            </label>
            <button
              type="button"
              onClick={handleDownloadSampleJson}
              className="flex items-center px-4 py-2 bg-white text-green-600 rounded-lg border border-green-600 hover:bg-green-50"
            >
              <Download className="w-4 h-4 mr-2" />
              <span>Download Sample</span>
            </button>
            {jsonError && (
              <p className="text-red-500 text-sm">{jsonError}</p>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Upload a JSON file containing your survey structure, or download a sample template
          </p>
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">Questions</h3>
          <AnimatePresence>
            {surveyData.questions.map((question, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg mb-4"
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-md font-medium text-gray-700">Question {index + 1}</h4>
                  <button
                    type="button"
                    onClick={() => removeQuestion(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                <input
                  type="text"
                  value={question.question_text}
                  onChange={(e) => handleQuestionChange(index, 'question_text', e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 mb-3 py-2.5 px-3.5 text-gray-800"
                  placeholder="Enter question text"
                />
                <select
                  value={question.question_type}
                  onChange={(e) => handleQuestionChange(index, 'question_type', e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 mb-3 py-2.5 px-3.5 text-gray-800"
                >
                  {Object.values(QUESTION_TYPES)
                    .filter(type => !EXCLUDED_QUESTION_TYPES.includes(type))
                    .map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))
                  }
                </select>
                {needsOptions(question.question_type) && (
                  <div className="mt-2">
                    <h5 className="text-sm font-medium text-gray-700 mb-1">Options</h5>
                    {question.question_type === 'Rating Scale' ? (
                      <div className="space-y-2">
                        <div className="flex gap-4">
                          <div>
                            <label className="text-sm text-gray-600">Min</label>
                            <input
                              type="number"
                              value={question.options?.min || 0}
                              onChange={(e) => handleQuestionChange(index, 'options', {
                                ...question.options,
                                min: parseInt(e.target.value)
                              })}
                              className="w-20 border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                          <div>
                            <label className="text-sm text-gray-600">Max</label>
                            <input
                              type="number"
                              value={question.options?.max || 5}
                              onChange={(e) => handleQuestionChange(index, 'options', {
                                ...question.options,
                                max: parseInt(e.target.value)
                              })}
                              className="w-20 border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-600 block mb-2">Labels (one per line)</label>
                          <textarea
                            value={question.options?.labels?.join('\n') || ''}
                            onChange={(e) => {
                              // Store the raw value and only process when editing is done
                              const rawValue = e.target.value;
                              handleQuestionChange(index, 'options', {
                                ...question.options,
                                labels: rawValue.split('\n').filter(label => label !== undefined)
                              });
                            }}
                            className="w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 p-3 text-gray-800 leading-relaxed"
                            rows={4}
                            placeholder="Enter labels, one per line"
                            onKeyDown={(e) => {
                              // Ensure Enter key works properly
                              if (e.key === 'Enter') {
                                // Let the default enter behavior happen naturally
                                return true;
                              }
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className="text-sm text-gray-600 block mb-2">Options (one per line)</label>
                        <textarea
                          value={Array.isArray(question.options) ? question.options.join('\n') : ''}
                          onChange={(e) => {
                            // Store the raw text value directly, don't immediately split/filter
                            const rawValue = e.target.value;
                            // Only process into array when done editing
                            handleQuestionChange(index, 'options', 
                              rawValue.split('\n').filter(opt => opt !== undefined)
                            );
                          }}
                          className="w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 p-3 text-gray-800 leading-relaxed"
                          rows={4}
                          placeholder="Enter options, one per line"
                          onKeyDown={(e) => {
                            // Ensure Enter key works properly
                            if (e.key === 'Enter') {
                              // Let the default enter behavior happen naturally
                              return true;
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
          <button
            type="button"
            onClick={addQuestion}
            className="mt-2 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm dark:shadow-gray-900/20 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Question
          </button>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm dark:shadow-gray-900/20 text-sm font-medium rounded-md text-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm dark:shadow-gray-900/20 text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {loading ? 'Creating...' : (survey ? 'Update Survey' : 'Create Survey')}
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default SurveyBuilder;